// src/components/themes/neon_studio/admin/BranchLogin.tsx
"use client";

import { Terminal, Lock, Mail, Eye, EyeOff, Loader2, Zap } from "lucide-react";

interface BranchLoginProps {
  branchSlug: string;
  branchName?: string;
  email: string;
  setEmail: (val: string) => void;
  password: string;
  setPassword: (val: string) => void;
  showPassword: boolean;
  setShowPassword: (val: boolean) => void;
  loading: boolean;
  error: string | null;
  onSubmit: (e: React.FormEvent) => void;
}

export default function NeonStudioBranchLogin({
  branchSlug,
  branchName,
  email,
  setEmail,
  password,
  setPassword,
  showPassword,
  setShowPassword,
  loading,
  error,
  onSubmit,
}: BranchLoginProps) {
  return (
    <div className="fixed inset-0 z-[99999] w-screen h-screen bg-[#05050a] text-cyan-400 font-mono flex items-center justify-center p-4 overflow-y-auto">
      {/* Background Neon Grid Glow */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-cyan-900/20 via-black to-black pointer-events-none" />

      <div className="w-full max-w-md bg-black/80 backdrop-blur-xl border-2 border-cyan-500/40 rounded-3xl p-6 sm:p-8 shadow-[0_0_50px_-10px_rgba(6,182,212,0.35)] space-y-6 relative my-auto">
        <div className="text-center space-y-2">
          <div className="w-14 h-14 rounded-2xl bg-cyan-500/10 border border-cyan-400/50 flex items-center justify-center mx-auto shadow-[0_0_20px_rgba(6,182,212,0.4)]">
            <Terminal className="w-7 h-7 text-cyan-400 animate-pulse" />
          </div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-cyan-500/20 text-cyan-300 text-[10px] font-black uppercase tracking-widest border border-cyan-500/50">
            <Zap className="w-3.5 h-3.5 text-cyan-400" />
            <span>NEON NODE GATEWAY</span>
          </div>
          <h1 className="text-xl font-black uppercase text-white tracking-wider">
            {branchName || branchSlug}
          </h1>
          <p className="text-[11px] text-cyan-300/70">
            // AUTHORIZED OUTLET OPERATORS ONLY
          </p>
        </div>

        {error && (
          <div className="p-3.5 rounded-xl bg-rose-500/20 border border-rose-500 text-rose-300 text-xs font-bold">
            [ACCESS_DENIED]: {error}
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-[11px] uppercase tracking-widest mb-1 text-cyan-400 font-black">
              &gt; OPERATOR_EMAIL
            </label>
            <div className="relative flex items-center">
              <Mail className="w-4 h-4 absolute left-3.5 text-cyan-500" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="operator@neon.net"
                className="w-full pl-10 pr-4 py-3 rounded-xl text-xs bg-slate-950 border border-cyan-500/50 text-cyan-200 outline-none focus:border-cyan-300 focus:shadow-[0_0_15px_rgba(6,182,212,0.5)] transition-all"
              />
            </div>
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-widest mb-1 text-cyan-400 font-black">
              &gt; ACCESS_KEY
            </label>
            <div className="relative flex items-center">
              <Lock className="w-4 h-4 absolute left-3.5 text-cyan-500" />
              <input
                type={showPassword ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••••"
                className="w-full pl-10 pr-10 py-3 rounded-xl text-xs bg-slate-950 border border-cyan-500/50 text-cyan-200 outline-none focus:border-cyan-300 focus:shadow-[0_0_15px_rgba(6,182,212,0.5)] transition-all"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 text-cyan-500 hover:text-cyan-300"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3.5 rounded-xl text-xs font-black uppercase tracking-widest text-black bg-cyan-400 hover:bg-cyan-300 shadow-[0_0_25px_rgba(6,182,212,0.6)] transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin text-black" />
                <span>AUTHENTICATING...</span>
              </>
            ) : (
              <span>INITIALIZE SESSION_</span>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}